<template>
  <div class="sticky bottom-0 z-10 -mx-6 mt-4 px-6 py-3 bg-white border-t border-gray-200 rounded-b-md flex flex-wrap items-center gap-x-4 gap-y-2">
    <!-- Estado del guardado: se anuncia sin mover el foco. -->
    <p class="flex items-center gap-2 text-sm text-gray-700" aria-live="polite">
      <font-awesome-icon v-if="saving" class="h-4 w-4 animate-spin motion-reduce:animate-none text-gray-500" icon="spinner"/>
      <span v-else class="h-2 w-2 rounded-full" :class="isDirty ? 'bg-amber-500' : 'bg-gray-300'" aria-hidden="true"></span>
      <span>{{ statusText }}</span>
    </p>

    <p v-if="conflict" class="flex items-center gap-2 text-sm text-red-700" role="alert">
      <font-awesome-icon icon="exclamation-triangle" class="h-4 w-4" aria-hidden="true"/>
      <span>{{ $t('projects.blocks_conflict_status') }}</span>
      <button type="button" v-on:click="$emit('resolve-conflict')"
              class="font-medium underline rounded-md hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-bpblue">
        {{ $t('projects.blocks_conflict_reload') }}
      </button>
    </p>

    <p v-if="errorCount" class="flex items-center gap-2 text-sm text-red-700" aria-live="polite">
      <span>{{ $t('projects.blocks_errors_count', {count: errorCount}, errorCount) }}</span>
      <button type="button" v-on:click="$emit('go-to-first-error')"
              class="font-medium underline rounded-md hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-bpblue">
        {{ $t('projects.blocks_go_to_first_error') }}
      </button>
    </p>

    <div class="ml-auto flex items-center gap-2">
      <span class="hidden md:inline text-xs text-gray-600">{{ $t('projects.save_shortcut_hint', {shortcut: shortcutLabel}) }}</span>
      <button type="button" v-on:click="$emit('discard')" :disabled="saving || !isDirty"
              class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-100 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bpblue">
        {{ $t('projects.discard_changes') }}
      </button>
      <button type="button" v-on:click="$emit('save')" :disabled="saving || !isDirty || conflict"
              :title="$t('projects.save_shortcut_title', {shortcut: shortcutLabel})" aria-keyshortcuts="Control+S Meta+S"
              class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-bpblue hover:bg-blue-500 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bpblue">
        <font-awesome-icon v-if="saving" class="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" icon="spinner"/>
        {{ $t('general.save') }}
      </button>
    </div>
  </div>
</template>

<script>
const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent)

// Barra fija del editor de bloques (FE-051): estado, errores y acciones de guardado.
export default {
  name: "BlocksSaveBar",
  props: {
    isDirty: {
      type: Boolean,
      required: true
    },
    saving: {
      type: Boolean,
      required: true
    },
    savedAt: {
      type: Date,
      default: null
    },
    errorCount: {
      type: Number,
      default: 0
    },
    // Otra persona ha guardado la lección (409): no se puede guardar sin recargar.
    conflict: {
      type: Boolean,
      default: false
    }
  },
  emits: ['save', 'discard', 'go-to-first-error', 'resolve-conflict'],
  computed: {
    shortcutLabel() {
      return isMac ? '⌘S' : 'Ctrl+S'
    },
    statusText() {
      if (this.saving) return this.$t('projects.blocks_status_saving')
      if (this.isDirty) return this.$t('projects.blocks_status_dirty')
      if (this.savedAt) {
        const time = this.savedAt.toLocaleTimeString(this.$i18n.locale, {hour: '2-digit', minute: '2-digit'})
        return this.$t('projects.blocks_status_saved_at', {time})
      }
      return this.$t('projects.blocks_status_clean')
    }
  }
}
</script>
